/* eslint-disable @typescript-eslint/no-explicit-any */
import { isEqual } from 'lodash'
import { QueryParameters } from '@deck.gl/carto'
import { addDataToMap } from '@carto/kepler.gl/actions'
import { RequestStatus } from 'features/common'
import { updateCurrentMapDataset } from 'features/maps/state/mapsSlice'
import { call, put, select } from 'redux-saga/effects'
import { TilesetLayerLoader } from './tilesetLoader'
import { showSQLPanel } from '../builderSlice'
import { getkeplerDatasetById, getLayerByDatasetId, getLayerToBeMergedByDatasetId } from '../keplerGlSlice'
import { isDynamicSpatialIndexDataset, shouldLoadAsDynamicTiling } from '../builderSliceUtils'
import { DEFAULT_AGGREGATION_EXP } from 'features/builder/ui/builderConstants'
import { RootState } from 'app/state/store'
import { getAggregationExp } from '../spatialIndexSagas'
import { ProviderType } from 'features/connections'
import { parseQuery } from 'features/builder/utils/queryParametersUtils'
import { LayerLoaderProps } from './layerLoader'
import { Formats, MapType, SqlParameter } from 'features/maps/types'
import { loadDataForWidgets } from '../WidgetsSagas'
import { datasetRequiresColumns, getDatasetColumnsFromMap, getDatasetDefaultColumns } from '../datasetUtils'
import { patchDataset } from 'features/maps/api/maps'
import { updateTilesetDatasetTilejson } from '../keplerGlActions'
import { KeplerTable } from 'features/builder/types'
import { isAgentDataset } from 'features/builder/model/agent'

interface QueryLayerLoaderProp extends LayerLoaderProps {
  sqlParameters?: SqlParameter[]
}

export class QueryLayerLoader extends TilesetLayerLoader {
  sqlParameters: SqlParameter[]

  constructor(props: QueryLayerLoaderProp) {
    super(props)
    this.sqlParameters = props.sqlParameters || []
  }

  *add(): Generator<any, any, any> {
    this.processSource({
      query: this.source,
      sqlParameters: this.sqlParameters,
      provider: this.provider
    })
    yield super.add()
  }

  processSource({
    query,
    sqlParameters,
    provider
  }: {
    query: QueryLayerLoader['source']
    sqlParameters: SqlParameter[]
    provider: QueryLayerLoader['provider']
  }) {
    const { source, queryTemplate, queryParameters } = parseQuery({ query, sqlParameters, provider })
    this.source = source
    this.queryTemplate = queryTemplate
    this.queryParameters = queryParameters
  }

  *_add() {
    yield this._saveDataset()

    try {
      yield put(
        showSQLPanel({
          datasetId: this.datasetId,
          mapId: this.mapId,
          minimized: this.minimized,
          fromAnalysis: this.fromAnalysis
        })
      )
    } catch (error) {
      throw new Error(`Cannot show SQL Panel ${(error as Error).message}`)
    }

    yield this._fetchData()
    yield this._setupColumnsForNewDataset()
    const datasetStillExists = yield this.datasetExists()
    if (!datasetStillExists) {
      return false
    }

    yield this._addDataset()
    if (this.createLayer) {
      yield this._createLayer()
    }

    return true
  }

  *updateQuery({
    query,
    sqlParameters,
    format,
    provider,
    geoColumn,
    skipSaveDataset = false
  }: {
    query: string
    sqlParameters: SqlParameter[]
    format?: Formats
    provider: ProviderType
    geoColumn: string | null
    skipSaveDataset?: boolean
  }) {
    try {
      const oldDataset = yield select((state: RootState) => getkeplerDatasetById(state, this.datasetId))
      // oldDataset could not exist because it failed during the map load
      const keplerDatasetWasLoaded = !!oldDataset
      const loadAsDynamicTiling = shouldLoadAsDynamicTiling({ type: this.type, format })

      this.processSource({
        query,
        sqlParameters,
        provider: provider
      })

      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: {
            type: this.type,
            source: this.source,
            queryParameters: this.queryParameters,
            queryTemplate: this.queryTemplate,
            format: format || this.format,
            geoColumn,
            status: RequestStatus.Pending,
            errorMessage: undefined,
            runningSince: Date.now(),
            runningTo: undefined
          }
        })
      )

      yield this._fetchData()

      if (!this.dataset || !this.datasetId) throw new Error('Not dataset initialized')

      if (!keplerDatasetWasLoaded) {
        // We load the new Kepler dataset using the result of _fetchDataWithCacheControl.
        // This is necessary because some of the following code, such as _refreshAggregationExpAndColumns,
        // depends on the Kepler dataset being available.
        yield put(
          addDataToMap({
            datasets: [this.dataset],
            options: { autoCreateLayers: false, keepExistingConfig: true, centerMap: false }
          })
        )
      }

      yield this._refreshAggregationExpAndColumns()

      yield this._updateDataset({
        type: this.type,
        query: this.source,
        queryParameters: this.queryParameters,
        queryTemplate: this.queryTemplate,
        format,
        geoColumn,
        aggregationExp: this.aggregationExp,
        columns: this.columns,
        skipSaveDataset
      })

      const exist = yield existLayer(this.datasetId)

      if (!exist) {
        // The dataset is not added. Maybe because the first query failed, we need to add it
        yield this._addDataset()
        if (this.createLayer) {
          yield this._createLayer()
        }
      } else {
        // The dataset was added, we need to refresh it
        const { tilejson, schema } = this.dataset?.metadata || {}
        yield put(
          updateTilesetDatasetTilejson({
            datasetId: this.datasetId,
            tilejson,
            schema
          })
        )
        yield this._refreshLayers()
      }

      if (loadAsDynamicTiling) {
        // If the Kepler dataset failed to load during the map initialization, _refreshStats will not work
        // because it relies on the oldDataset to determine which stats to request. In this case, we read the stats directly from the layers
        keplerDatasetWasLoaded ? yield this._refreshStats(oldDataset) : yield this._refreshKeplerVisLayerStats()
      }

      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: {
            aggregationExp: this.aggregationExp,
            status: RequestStatus.Fulfilled,
            runningTo: Date.now(),
            errorMessage: undefined,
            // We remove any reference to the workflow that generated the dataset. Util for Query This Table action
            sourceWorkflowId: null
          }
        })
      )

      yield loadDataForWidgets(yield this.getCartoData(), this.dataset as unknown as KeplerTable)
    } catch (error) {
      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: {
            status: RequestStatus.Rejected,
            errorMessage: (error as Error).message,
            runningTo: Date.now()
          }
        })
      )
      yield put(
        updateTilesetDatasetTilejson({
          datasetId: this.datasetId,
          tilejson: null,
          schema: null
        })
      )
      if (this.queryParameters) {
        yield this._updateDataset({
          query: this.source,
          queryParameters: this.queryParameters,
          format,
          skipSaveDataset
        })
      }
    }
  }

  *cancelQuery() {
    try {
      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: {
            status: RequestStatus.Cancelled,
            errorMessage: undefined,
            runningTo: Date.now()
          }
        })
      )
    } catch (error) {
      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: {
            status: RequestStatus.Rejected,
            errorMessage: (error as Error).message,
            runningTo: Date.now()
          }
        })
      )
    }
  }

  *_updateDataset({
    type,
    query,
    format,
    geoColumn,
    aggregationExp,
    queryParameters,
    queryTemplate,
    columns,
    skipSaveDataset
  }: {
    type?: MapType
    query: string
    format?: Formats
    geoColumn?: string | null
    aggregationExp?: string | null
    queryParameters?: QueryParameters | null
    queryTemplate?: string | null
    columns?: string[] | null
    skipSaveDataset: boolean
  }) {
    try {
      // Query parameters can be updated in public maps and in this case we have not permissions to edit at server
      if (skipSaveDataset) return

      yield call(patchDataset, {
        mapId: this.mapId,
        datasetId: this.datasetId,
        body: {
          type,
          source: query,
          queryTemplate,
          format,
          geoColumn,
          columns,
          aggregationExp,
          queryParameters,
          // We remove any reference to the workflow that generated the dataset. Util for Query This Table action
          sourceWorkflowId: null
        }
      })
    } catch (error) {
      throw new Error(`Error updating dataset at server ${(error as Error).message}`)
    }
  }

  *_setupColumnsForNewDataset() {
    try {
      if (!datasetRequiresColumns(this)) {
        return
      }

      const schema = this.dataset?.metadata?.schema
      const columns = getDatasetDefaultColumns({ geoColumn: this.geoColumn, schema })

      if (!isEqual(columns, this.columns)) {
        this.columns = columns
        yield this._fetchData()
        yield put(updateCurrentMapDataset({ id: this.datasetId, dataset: { columns } }))
        if (!isAgentDataset(this.datasetId)) {
          yield call(patchDataset, { mapId: this.mapId, datasetId: this.datasetId, body: { columns } })
        }
      }
    } catch (error) {
      console.error('_setupColumnsForNewDataset failed', error)
    }
  }

  *_refreshAggregationExpAndColumns() {
    try {
      const fields = this.dataset?.metadata?.schema?.map((f) => f.name) as string[] | undefined

      if (!fields) {
        return
      }

      const isDynamicSpatialIndex = isDynamicSpatialIndexDataset({
        type: this.type,
        format: this.format,
        geoColumn: this.geoColumn
      })

      const aggregationExp = isDynamicSpatialIndex
        ? (yield getAggregationExp(this.datasetId, fields)) || DEFAULT_AGGREGATION_EXP
        : this.aggregationExp

      const columns = yield getDatasetColumnsFromMap(this.datasetId, fields)
      if (aggregationExp !== this.aggregationExp || !isEqual(columns, this.columns)) {
        this.aggregationExp = aggregationExp
        this.columns = columns
        yield this._fetchData()
      }
    } catch (error) {
      throw new Error(`Error getting the aggregation expression ${(error as Error).message}`)
    }
  }
}

function* existLayer(datasetId: string) {
  try {
    if (yield select(getLayerByDatasetId, datasetId)) return true

    if (yield select(getLayerToBeMergedByDatasetId, datasetId)) return true

    return false
  } catch (error) {
    throw new Error('Exist layer')
  }
}
